import React, { useState } from 'react';
import { Send } from 'lucide-react';
const SERVICE_AREAS = ['Orlando', 'Kissimmee', 'Broward', 'Davenport', 'Lake Nona', 'Winter Garden', 'Celebration', 'Windermere', 'St. Cloud', 'Winter Park'];
const SERVICES = ['Move-Out Cleaning', 'Move-In Cleaning', 'Post-Construction Cleaning', 'Deep Cleaning', 'Commercial Cleaning', 'Airbnb Turnover Cleaning'];
export function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    service: '',
    propertyType: 'House',
    sqft: '',
    bedrooms: '',
    bathrooms: '',
    occupancy: 'Vacant',
    date: '',
    condition: 'Average',
    message: ''
  });
  const [addons, setAddons] = useState<string[]>([]);
  const handleAddonToggle = (addon: string) => {
    setAddons(prev => prev.includes(addon) ? prev.filter(a => a !== addon) : [...prev, addon]);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = `*New Quote Request - NÍTIDO PRO*%0A
*Name:* ${formData.name}
*Phone:* ${formData.phone}
*Email:* ${formData.email}
*City:* ${formData.city}
*Service:* ${formData.service}
*Property:* ${formData.propertyType} (${formData.sqft} sqft)
*Rooms:* ${formData.bedrooms} Bed / ${formData.bathrooms} Bath
*Status:* ${formData.occupancy}
*Condition:* ${formData.condition}
*Preferred Date:* ${formData.date}
*Add-ons:* ${addons.length > 0 ? addons.join(', ') : 'None'}
*Message:* ${formData.message || 'None'}`;
    window.open(`https://wa.me/19548181392?text=${text}`, '_blank');
  };
  const inputClass = 'w-full bg-transparent border-b border-concrete/30 text-paper py-3 px-0 focus:border-lime focus:outline-none transition-colors font-light text-sm placeholder:text-concrete/30 rounded-none appearance-none';
  const labelClass = 'block text-[10px] uppercase tracking-[0.2em] text-concrete/50 mb-1';
  // Native dropdown options render in the OS popup — force dark, readable text on a clean light background.
  const optionStyle = {
    color: '#0B0B0B',
    backgroundColor: '#F7F7F5'
  };
  return <section id="quote" className="py-32 bg-ink relative" data-id="element-179">
      <div className="container mx-auto px-8 lg:px-12 max-w-4xl" data-id="element-180">
        <div className="mb-20 text-center" data-id="element-181">
          <div className="flex items-center justify-center gap-4 mb-6" data-id="element-182">
            <div className="w-8 h-[1px] bg-lime" data-id="element-183"></div>
            <span className="text-paper/50 font-body text-xs uppercase tracking-[0.3em]" data-id="element-184">
              Inquiry
            </span>
            <div className="w-8 h-[1px] bg-lime" data-id="element-185"></div>
          </div>
          <h2 className="text-4xl md:text-5xl text-paper font-light mb-6" data-id="element-186">
            Get a <span className="font-medium" data-id="element-187">Free Quote.</span>
          </h2>
          <p className="text-concrete/60 font-light text-sm" data-id="element-188">
            Provide your property details and we'll text you back with a precise
            estimate.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-16" data-id="element-189">
          {/* Contact Info */}
          <div data-id="element-190">
            <h3 className="text-sm text-lime uppercase tracking-[0.2em] mb-8 font-medium" data-id="element-191">
              01. Contact Info
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-192">
              <div data-id="element-193">
                <label className={labelClass} data-id="element-194">Full Name *</label>
                <input required type="text" name="name" value={formData.name} onChange={handleChange} className={inputClass} data-id="element-195" />
              </div>
              <div data-id="element-196">
                <label className={labelClass} data-id="element-197">Phone Number *</label>
                <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-198" />
              </div>
              <div data-id="element-199">
                <label className={labelClass} data-id="element-200">Email Address *</label>
                <input required type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} data-id="element-201" />
              </div>
              <div data-id="element-202">
                <label className={labelClass} data-id="element-203">City *</label>
                <select required name="city" value={formData.city} onChange={handleChange} className={`${inputClass} text-paper bg-ink`} data-id="element-204">
                  <option value="" disabled style={optionStyle} data-id="element-205">
                    Select City...
                  </option>
                  {SERVICE_AREAS.map(city => <option key={city} value={city} style={optionStyle} data-id="element-206">
                      {city}
                    </option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div data-id="element-207">
            <h3 className="text-sm text-lime uppercase tracking-[0.2em] mb-8 font-medium" data-id="element-208">
              02. Service Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-209">
              <div className="md:col-span-2" data-id="element-210">
                <label className={labelClass} data-id="element-211">Type of Cleaning *</label>
                <select required name="service" value={formData.service} onChange={handleChange} className={`${inputClass} text-paper bg-ink`} data-id="element-212">
                  <option value="" disabled style={optionStyle} data-id="element-213">
                    Select Service...
                  </option>
                  {SERVICES.map(s => <option key={s} value={s} style={optionStyle} data-id="element-214">
                      {s}
                    </option>)}
                </select>
              </div>

              <div data-id="element-215">
                <label className={labelClass} data-id="element-216">Property Type</label>
                <select name="propertyType" value={formData.propertyType} onChange={handleChange} className={`${inputClass} text-paper bg-ink`} data-id="element-217">
                  <option style={optionStyle} data-id="element-218">House</option>
                  <option style={optionStyle} data-id="element-219">Apartment / Condo</option>
                  <option style={optionStyle} data-id="element-220">Townhouse</option>
                  <option style={optionStyle} data-id="element-221">Commercial Space</option>
                </select>
              </div>
              <div data-id="element-222">
                <label className={labelClass} data-id="element-223">Square Footage *</label>
                <input required type="number" name="sqft" placeholder="e.g. 1500" value={formData.sqft} onChange={handleChange} className={inputClass} data-id="element-224" />
              </div>

              <div className="grid grid-cols-2 gap-8" data-id="element-225">
                <div data-id="element-226">
                  <label className={labelClass} data-id="element-227">Bedrooms</label>
                  <input type="number" name="bedrooms" min="0" value={formData.bedrooms} onChange={handleChange} className={inputClass} data-id="element-228" />
                </div>
                <div data-id="element-229">
                  <label className={labelClass} data-id="element-230">Bathrooms</label>
                  <input type="number" name="bathrooms" min="0" step="0.5" value={formData.bathrooms} onChange={handleChange} className={inputClass} data-id="element-231" />
                </div>
              </div>

              <div data-id="element-232">
                <label className={labelClass} data-id="element-233">Occupancy</label>
                <div className="flex gap-8 pt-3" data-id="element-234">
                  <label className="flex items-center gap-3 text-paper font-light text-sm cursor-pointer" data-id="element-235">
                    <input type="radio" name="occupancy" value="Vacant" checked={formData.occupancy === 'Vacant'} onChange={handleChange} className="accent-lime w-4 h-4" data-id="element-236" />{' '}
                    Vacant
                  </label>
                  <label className="flex items-center gap-3 text-paper font-light text-sm cursor-pointer" data-id="element-237">
                    <input type="radio" name="occupancy" value="Occupied" checked={formData.occupancy === 'Occupied'} onChange={handleChange} className="accent-lime w-4 h-4" data-id="element-238" />{' '}
                    Occupied
                  </label>
                </div>
              </div>

              <div data-id="element-239">
                <label className={labelClass} data-id="element-240">Preferred Date</label>
                <input type="date" name="date" value={formData.date} onChange={handleChange} style={{
                colorScheme: 'dark'
              }} className={`${inputClass} cursor-pointer`} data-id="element-241" />
              </div>

              <div data-id="element-242">
                <label className={labelClass} data-id="element-243">Property Condition</label>
                <select name="condition" value={formData.condition} onChange={handleChange} className={`${inputClass} text-paper bg-ink`} data-id="element-244">
                  <option style={optionStyle} data-id="element-245">Lightly Soiled</option>
                  <option style={optionStyle} data-id="element-246">Average</option>
                  <option style={optionStyle} data-id="element-247">Heavy Duty / Very Dirty</option>
                </select>
              </div>
            </div>
          </div>

          {/* Add-ons & Extras */}
          <div data-id="element-248">
            <h3 className="text-sm text-lime uppercase tracking-[0.2em] mb-8 font-medium" data-id="element-249">
              03. Add-ons & Extras
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-250">
              {['Inside Fridge', 'Inside Oven', 'Interior Windows', 'Baseboards Detail', 'Blinds Detail', 'Garage Sweep'].map(addon => <label key={addon} className="flex items-center gap-3 text-concrete/80 font-light text-sm cursor-pointer hover:text-paper transition-colors" data-id="element-251">
                  <input type="checkbox" checked={addons.includes(addon)} onChange={() => handleAddonToggle(addon)} className="accent-lime w-4 h-4 rounded-none" data-id="element-252" />
                  {addon}
                </label>)}
            </div>
          </div>

          {/* Message & Submit */}
          <div className="pt-8 border-t border-concrete/10" data-id="element-253">
            <label className={labelClass} data-id="element-254">Additional Details or Message</label>
            <textarea name="message" rows={3} value={formData.message} onChange={handleChange} className={`${inputClass} mb-12 resize-none`} data-id="element-255"></textarea>

            <button type="submit" className="w-full bg-paper text-ink py-5 text-xs uppercase tracking-[0.2em] hover:bg-concrete transition-colors flex items-center justify-center gap-3 group" data-id="element-256">
              <Send size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-257" />
              Send Request via WhatsApp
            </button>
            <p className="text-center text-concrete/40 text-[10px] uppercase tracking-widest mt-6" data-id="element-258">
              Submitting will open WhatsApp with your pre-filled details.
            </p>
          </div>
        </form>
      </div>
    </section>;
}